import React, { useState, useMemo } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    Badge,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Alert,
    AlertIcon,
    IconButton,
    Tooltip,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Collapse,
    Checkbox,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
} from '@chakra-ui/react';
import {
    MdSearch,
    MdDescription,
    MdEdit,
    MdDelete,
    MdRefresh,
    MdMoreVert,
    MdFilterList,
    MdExpandMore,
    MdExpandLess,
    MdDriveFileRenameOutline
} from 'react-icons/md';
import { useDocuments } from './hooks/useDocuments';
import ConfirmationModal from './ConfirmationModal';
import { useOcrTierOptions } from './hooks/useOptions';

const DocumentList = ({ onEditDocument }) => {
    const {
        documents,
        processingDocuments,
        error,
        deleteDocument,
        retryDocument,
        fetchDocuments,
        renameDocument
    } = useDocuments();

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, document: null });
    const [selected, setSelected] = useState(new Set());
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [renameState, setRenameState] = useState({ isOpen: false, document: null, value: '' });
    const { isOpen: isFiltersOpen, onToggle: onToggleFilters } = useDisclosure();
    const { options: ocrTierOptions } = useOcrTierOptions();

    // Create a helper function to get display name from tier slug
    const getOcrTierDisplayName = (tierSlug) => {
        const tier = ocrTierOptions.find(option => option.value === tierSlug);
        return tier ? tier.label : tierSlug; // fallback to slug if not found
    };

    // Filter and search documents
    const filteredDocuments = useMemo(() => {
        let filtered = documents;

        // Apply search filter
        if (searchTerm.trim()) {
            filtered = filtered.filter(doc =>
                doc.filename.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(doc => doc.status === statusFilter);
        }

        // Sort by created_at (newest first)
        return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [documents, searchTerm, statusFilter]);

    // Status counts for filter badges
    const statusCounts = useMemo(() => {
        return documents.reduce((acc, doc) => {
            acc[doc.status] = (acc[doc.status] || 0) + 1;
            return acc;
        }, {});
    }, [documents]);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status, isProcessing = false) => {
        if (isProcessing) {
            return (
                <HStack spacing={1}>
                    <Spinner size="xs" />
                    <Badge colorScheme="blue" size="sm">Processing</Badge>
                </HStack>
            );
        }

        switch (status) {
            case 'processing':
                return <Badge colorScheme="blue" size="sm">Processing</Badge>;
            case 'completed':
                return <Badge colorScheme="green" size="sm">Ready</Badge>;
            case 'failed':
                return <Badge colorScheme="red" size="sm">Failed</Badge>;
            default:
                return <Badge size="sm">{status}</Badge>;
        }
    };

    const handleEdit = (document) => {
        if (onEditDocument) {
            onEditDocument(document);
        }
    };

    const handleDeleteClick = (document) => {
        setDeleteConfirm({ isOpen: true, document });
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirm.document) {
            await deleteDocument(deleteConfirm.document.id);
        }
        setDeleteConfirm({ isOpen: false, document: null });
    };

    const handleRetry = async (documentId) => {
        await retryDocument(documentId);
    };

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <VStack align="flex-start" spacing={1}>
                    <Text fontWeight="500">Failed to load documents</Text>
                    <Text fontSize="sm">{error}</Text>
                </VStack>
                <Button 
                    size="sm" 
                    variant="outline" 
                    ml="auto"
                    onClick={fetchDocuments}
                >
                    Retry
                </Button>
            </Alert>
        );
    }

    // Selection logic
    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelected(new Set());
    const selectAllVisible = () => {
        const ids = filteredDocuments.map(d => d.id);
        setSelected(new Set(ids));
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        setIsBulkDeleting(true);
        try {
            // Perform deletes sequentially to keep UI consistent with toasts
            for (const id of selected) {
                try { await deleteDocument(id); } catch (_) { /* toast already shown */ }
            }
        } finally {
            setIsBulkDeleting(false);
            clearSelection();
        }
    };

    // Rename
    const openRename = (doc) => setRenameState({ isOpen: true, document: doc, value: doc.filename });
    const closeRename = () => setRenameState({ isOpen: false, document: null, value: '' });
    const confirmRename = async () => {
        if (!renameState.document) return;
        try {
            await renameDocument(renameState.document.id, renameState.value);
            closeRename();
        } catch (_) {
            // toast shown by hook
        }
    };


    return (
        <VStack spacing={4} align="stretch">
            {/* Header with Search and Filters */}
            <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                    <HStack spacing={4} flex="1">
                        <InputGroup maxW="300px">
                            <InputLeftElement pointerEvents="none">
                                <MdSearch color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search document titles"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg="white"
                            />
                        </InputGroup>

                        <Button
                            leftIcon={<MdFilterList />}
                            rightIcon={isFiltersOpen ? <MdExpandLess /> : <MdExpandMore />}
                            variant="outline"
                            size="sm"
                            onClick={onToggleFilters}
                        >
                            Filters
                        </Button>
                    </HStack>

                    <Tooltip label="Refresh documents">
                        <IconButton
                            icon={<MdRefresh />}
                            variant="outline"
                            size="sm"
                            onClick={fetchDocuments}
                            aria-label="Refresh documents"
                        />
                    </Tooltip>
                </HStack>

                {/* Filter Panel */}
                <Collapse in={isFiltersOpen} animateOpacity>
                    <Box bg="gray.50" p={4} borderRadius="md" border="1px" borderColor="gray.200">
                        <VStack align="flex-start" spacing={3}>
                            <Text fontSize="sm" fontWeight="500" color="gray.700">
                                Filter by Status
                            </Text>
                            <HStack spacing={2} flexWrap="wrap">
                                <Button
                                    size="xs"
                                    variant={statusFilter === 'all' ? 'solid' : 'outline'}
                                    colorScheme={statusFilter === 'all' ? 'brand' : 'gray'}
                                    onClick={() => setStatusFilter('all')}
                                >
                                    All ({documents.length})
                                </Button>
                                {Object.entries(statusCounts).map(([status, count]) => (
                                    <Button
                                        key={status}
                                        size="xs"
                                        variant={statusFilter === status ? 'solid' : 'outline'}
                                        colorScheme={statusFilter === status ? 'brand' : 'gray'}
                                        onClick={() => setStatusFilter(status)}
                                    >
                                        {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
                                    </Button>
                                ))}
                            </HStack>
                        </VStack>
                    </Box>
                </Collapse>
            </VStack>

            {/* Bulk actions toolbar */}
            {selected.size > 0 && (
                <HStack bg="gray.50" p={3} borderRadius="md" border="1px" borderColor="gray.200" justify="space-between">
                    <HStack spacing={3}>
                        <Text fontSize="sm">{selected.size} selected</Text>
                        <Button size="sm" variant="outline" onClick={selectAllVisible}>
                            Select all ({filteredDocuments.length})
                        </Button>
                        <Button size="sm" variant="ghost" onClick={clearSelection}>
                            Clear
                        </Button>
                    </HStack>
                    <Button
                        size="sm"
                        colorScheme="red"
                        onClick={handleBulkDelete}
                        isLoading={isBulkDeleting}
                        loadingText="Deleting..."
                    >
                        Delete Selected
                    </Button>
                </HStack>
            )}

            {/* Documents List */}
            {filteredDocuments.length > 0 ? (
                <VStack spacing={2} align="stretch">
                    {filteredDocuments.map((document) => {
                        const isProcessing = processingDocuments.has(document.id);
                        
                        return (
                            <Box
                                key={document.id}
                                bg="white"
                                p={4}
                                borderRadius="md"
                                border="1px"
                                borderColor="gray.200"
                                _hover={{ shadow: 'sm', borderColor: 'gray.300' }}
                                transition="all 0.2s"
                            >
                                <HStack justify="space-between" align="flex-start">
                                    <HStack spacing={3} flex="1" minW="0">
                                        <Checkbox
                                            isChecked={selected.has(document.id)}
                                            onChange={() => toggleSelect(document.id)}
                                            mt="2px"
                                        />
                                        <MdDescription size="20px" color="gray.600" />
                                        <VStack align="flex-start" spacing={1} flex="1" minW="0">
                                            <HStack justify="space-between" w="full">
                                                <Text
                                                    fontWeight="500"
                                                    fontSize="md"
                                                    isTruncated
                                                    flex="1"
                                                >
                                                    {document.filename}
                                                </Text>
                                                {getStatusBadge(document.status, isProcessing)}
                                            </HStack>
                                            
                                            <HStack spacing={4} fontSize="xs" color="gray.500">
                                                <Text  fontSize="xs" color="gray.500">{formatFileSize(document.file_size)}</Text>
                                                <Text  fontSize="xs" color="gray.500">
                                                    {getOcrTierDisplayName(document.ocr_tier)} OCR
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">{formatDate(document.created_at)}</Text>
                                            </HStack>

                                            {document.status === 'failed' && document.error_message && (
                                                <Alert status="error" size="sm" borderRadius="sm">
                                                    <AlertIcon boxSize="12px" />
                                                    <Text fontSize="xs">{document.error_message}</Text>
                                                </Alert>
                                            )}
                                        </VStack>
                                    </HStack>

                                    <Menu>
                                        <MenuButton
                                            as={IconButton}
                                            icon={<MdMoreVert />}
                                            variant="ghost"
                                            size="sm"
                                            aria-label="Document actions"
                                        />
                                        <MenuList>
                                            <MenuItem
                                                icon={<MdDriveFileRenameOutline />}
                                                onClick={() => openRename(document)}
                                            >
                                                Rename
                                            </MenuItem>
                                            {document.status === 'completed' && (
                                                <MenuItem
                                                    icon={<MdEdit />}
                                                    onClick={() => handleEdit(document)}
                                                >
                                                    Edit Text
                                                </MenuItem>
                                            )}
                                            {document.status === 'failed' && (
                                                <MenuItem
                                                    icon={<MdRefresh />}
                                                    onClick={() => handleRetry(document.id)}
                                                >
                                                    Retry Processing
                                                </MenuItem>
                                            )}
                                            <MenuItem
                                                icon={<MdDelete />}
                                                onClick={() => handleDeleteClick(document)}
                                                color="red.600"
                                            >
                                                Delete
                                            </MenuItem>
                                        </MenuList>
                                    </Menu>
                                </HStack>
                            </Box>
                        );
                    })}
                </VStack>
            ) : (
                <Box
                    bg="gray.50"
                    p={8}
                    borderRadius="md"
                    textAlign="center"
                >
                    <VStack spacing={3}>
                        <MdDescription size="48px" color="gray.400" />
                        <VStack spacing={1}>
                            <Text fontWeight="500" color="gray.600">
                                {searchTerm || statusFilter !== 'all' 
                                    ? 'No documents match your search'
                                    : 'No documents yet'
                                }
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                {searchTerm || statusFilter !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Upload your first PDF document to get started'
                                }
                            </Text>
                        </VStack>
                    </VStack>
                </Box>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, document: null })}
                onConfirm={handleDeleteConfirm}
                title="Delete Document"
                message={`Are you sure you want to delete "${deleteConfirm.document?.filename}"? This action cannot be undone.`}
                confirmButtonText="Delete"
                confirmButtonColorScheme="red"
            />

            {/* Rename Modal */}
            <Modal isOpen={renameState.isOpen} onClose={closeRename} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Rename Document</ModalHeader>
                    <ModalBody>
                        <VStack align="stretch" spacing={2}>
                            <Text fontSize="sm" color="gray.600">Current name</Text>
                            <Text fontSize="sm" bg="gray.50" p={2} borderRadius="md" noOfLines={1}>
                                {renameState.document?.filename}
                            </Text>
                            <Text fontSize="sm" color="gray.600" mt={2}>New name</Text>
                            <Input
                                value={renameState.value}
                                onChange={(e) => setRenameState(s => ({ ...s, value: e.target.value }))}
                                placeholder="Enter new filename (e.g., report.pdf)"
                            />
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={closeRename}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={confirmRename}
                            isDisabled={!renameState.value.trim() || renameState.value === renameState.document?.filename}
                        >
                            Rename
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </VStack>
    );
};

export default DocumentList;